import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IPartnerFinSobRow } from '../../model'

import { getExpectedExitRows } from './getExpectedExitRows'
import { getRevenueMeasureField } from './getRevenueMeasureField'
import { getWeightedSob, TFinSobData } from './getWeightedSob'
import { sumMeasure } from './sumMeasure'
import { IRevenueAndSobExpectedExit, RevenueMeasureField, TCategoryRowScope } from './types'

/** No expected exit at all — while the actuals type is unset, or for a period with nothing left
 *  open, where callers show actuals instead. */
export const EMPTY_EXPECTED_EXIT: IRevenueAndSobExpectedExit = {
  sales: null,
  sob: null,
  actuals: { sales: null, sob: null },
  plan: { sales: null, sob: null },
}

interface IParams {
  rows: IPartnerFinSobRow[]
  /** The financial year the tile is showing. Read as-is, same as the plan. */
  selectedFYear: number
  /** The fiscal quarter (1–4) currently in progress — from `jbpSettings.currentFQuarter`, not
   *  `configuration`. Quarters before it read actuals; it and quarters after it read plan. */
  currentFQuarter: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  /** Defaults to `OVERALL_CATEGORY` (the offering total) when omitted. */
  category?: string
  categoryRowScope?: TCategoryRowScope
}

const toSobData = (rows: IPartnerFinSobRow[], measureField: RevenueMeasureField) =>
  rows.map<TFinSobData>((row) => ({
    sob: row.sob,
    revenue: row[measureField],
  }))

/** Adds the two halves up the way `sumMeasure` adds rows up: `null` only when neither half has
 *  data, so a real zero stays apart from "no data". */
const addSales = (actualsSales: number | null, planSales: number | null): number | null =>
  actualsSales === null && planSales === null ? null : (actualsSales ?? 0) + (planSales ?? 0)

/** The tile's expected exit column: what the partner is now forecast to end the year at —
 *  closed quarters as booked, the rest as planned. Each half is worked out on its own and comes
 *  back alongside the total, so the table can show what the total is made of.
 *
 *  Revenue is additive, so it is a plain sum: the total is the actuals half plus the plan half.
 *  SOB is a share, so it only rolls up as an average weighted by the same revenue measure — a big
 *  offering's share must count for more than a small one's. That is also why the total SOB is
 *  weighted across both halves' rows together rather than added up from the halves. */
export const getRevenueAndSobExpectedExit = ({
  rows,
  selectedFYear,
  currentFQuarter,
  actualsType,
  isLocalCurrency,
  category,
  categoryRowScope,
}: IParams): IRevenueAndSobExpectedExit => {
  const measureField = getRevenueMeasureField(actualsType, isLocalCurrency)

  if (measureField === null) {
    return EMPTY_EXPECTED_EXIT
  }

  const { actuals: actualsRows, plan: planRows } = getExpectedExitRows({
    rows,
    selectedFYear,
    currentFQuarter,
    category,
    categoryRowScope,
  })

  const actualsSales = sumMeasure(actualsRows, measureField)
  const planSales = sumMeasure(planRows, measureField)
  const totalSales = addSales(actualsSales, planSales)

  const actualsSobData = toSobData(actualsRows, measureField)
  const planSobData = toSobData(planRows, measureField)

  const actualsSob = getWeightedSob(actualsSobData)
  const planSob = getWeightedSob(planSobData)
  const totalSob = getWeightedSob([...actualsSobData, ...planSobData])

  return {
    sales: totalSales,
    sob: totalSob,
    actuals: { sales: actualsSales, sob: actualsSob },
    plan: { sales: planSales, sob: planSob },
  }
}
